import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Kicker, Reveal } from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import CtaBand from "@/components/CtaBand";
import { MARQUEE_ITEMS, MISSION_CHAPTERS } from "@/data/site";
import { useSiteData } from "@/context/SiteData";
import { usePageMeta } from "@/lib/meta";

export default function Mission() {
  usePageMeta(
    "The Mission | Diamond's Divine Arts",
    "Experience the Word through creativity. Diamond's Divine Arts is an artistic ministry illustrating biblical themes to strengthen faith."
  );
  const { t } = useSiteData();

  return (
    <>
      <section className="divine-rays relative overflow-hidden pb-20 pt-16 md:pb-28 md:pt-24" data-testid="mission-hero">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal>
            <Kicker>The Mission</Kicker>
          </Reveal>
          <h1 className="mt-6 max-w-4xl font-serif text-4xl font-light leading-[1.08] tracking-tight text-[#1C1815] sm:text-5xl lg:text-6xl">
            <Reveal delay={0.1}>
              Experience the Word through <span className="italic text-[#9A7A3F]">creativity.</span>
            </Reveal>
          </h1>
          <Reveal delay={0.2}>
            <p className="mt-8 max-w-2xl text-base font-light leading-relaxed text-[#4A423A]">
              {t(
                "mission_statement",
                "Diamond's Divine Arts is more than an art business. It is an artistic ministry — centered on faith, creativity, storytelling, and biblical inspiration. The mission is to push and strengthen the faith of the saints, using visual illustrations to equip people with the Word of God."
              )}
            </p>
          </Reveal>
        </div>
      </section>

      <Marquee items={MARQUEE_ITEMS} />

      <section className="py-20 md:py-32" data-testid="mission-chapters-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="space-y-0">
            {MISSION_CHAPTERS.map((c, i) => (
              <Reveal key={c.number} delay={i * 0.06}>
                <div
                  className="grid grid-cols-1 items-start gap-6 border-t border-[#D4AF37]/30 py-14 lg:grid-cols-12 lg:gap-12"
                  data-testid={`mission-chapter-${c.number}`}
                >
                  <div className="lg:col-span-4">
                    <span className="font-mono text-xs uppercase tracking-[0.3em] text-[#B89759]">
                      Chapter {c.number}
                    </span>
                    <h2 className="mt-3 font-serif text-2xl font-normal tracking-tight text-[#1C1815] sm:text-3xl">
                      {c.title}
                    </h2>
                  </div>
                  <p className="text-base font-light leading-relaxed text-[#4A423A] lg:col-span-7 lg:col-start-6">
                    {c.content}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal>
            <div className="mt-8 rounded-sm border border-[#D4AF37]/40 bg-[#F4EFE6]/80 p-10 text-center md:p-16">
              <p className="mx-auto max-w-2xl font-serif text-2xl font-light italic leading-relaxed text-[#2A241F] sm:text-3xl">
                “That the Word would not only be read — but seen, felt, and remembered.”
              </p>
              <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.3em] text-[#8C7E70]">
                The Heart of the Studio
              </p>
            </div>
          </Reveal>

          <Reveal className="mt-14 text-center">
            <Link to="/portfolio" className="btn-primary" data-testid="mission-portfolio-button">
              See the Work <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </section>

      <CtaBand />
    </>
  );
}
